// Shared slide-up order / booking form.
// Parent controls visibility via `visible` prop + animates translateY.
// Works both inside ItemCard (FeaturedItem) and as a fixed bottom sheet (WelcomePage zoomed view).

import React, { useState, useEffect, useRef } from 'react';
import { X, Minus, Plus, Navigation, ChevronDown, Building2, MapPin } from 'lucide-react';
import { getItemFormFields } from '../../utils/orderFormConfig';

const GOOGLE_LOGO = 'https://cdn.cdnlogo.com/logos/g/35/google-icon.svg';

// Inject spin keyframe once
if (typeof document !== 'undefined' && !document.getElementById('kk-spin-style')) {
  const s = document.createElement('style');
  s.id = 'kk-spin-style';
  s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

// Fields that get address autocomplete regardless of config source (covers Firestore-saved forms too)
const ADDRESS_KEYS = new Set(['pickupLocation', 'dropoffLocation', 'serviceAddress', 'eventAddress']);

// Lighten or darken a hex color so the form contrasts with the theme background.
// Dark themes get lighter; light themes get darker.
function tintColor(hex, amount) {
  const clean = (hex || '#141414').replace('#', '');
  if (clean.length !== 6) return hex || '#141414';
  const n = parseInt(clean, 16);
  const r = (n >> 16), g = ((n >> 8) & 0xff), b = (n & 0xff);
  const d = (r + g + b) / 3 < 128 ? amount : -amount;
  return `#${Math.min(255,Math.max(0,r+d)).toString(16).padStart(2,'0')}${Math.min(255,Math.max(0,g+d)).toString(16).padStart(2,'0')}${Math.min(255,Math.max(0,b+d)).toString(16).padStart(2,'0')}`;
}

function initValues(fields) {
  const v = {};
  fields.forEach(f => {
    if (f.type === 'stepper') v[f.key] = f.defaultVal ?? f.min ?? 1;
    else if (f.type === 'toggle') v[f.key] = false;
    else v[f.key] = '';
  });
  return v;
}

// ── Field renderers ────────────────────────────────────────────────────────────

function TextField({ field, value, onChange, textColor, inputBg, borderColor }) {
  return (
    <input
      type={field.type === 'text' ? 'text' : field.type}
      placeholder={field.placeholder || ''}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      className="kk-of-input"
      style={{
        background: inputBg,
        border: `1.5px solid ${borderColor}`,
        borderRadius: 8,
        padding: '0.42rem 0.65rem',
        color: textColor,
        fontSize: '0.72rem',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      }}
    />
  );
}

function AddressField({ field, value, onChange, textColor, inputBg, borderColor, dropdownBg, accent, shopName }) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const fetchSuggestions = (q) => {
    clearTimeout(debounceRef.current);
    if (q.length < 3) { setSuggestions([]); setSuggestOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        const results = data.map(d => d.display_name);
        setSuggestions(results);
        setSuggestOpen(results.length > 0);
      } catch {
        setSuggestions([]);
        setSuggestOpen(false);
      }
    }, 350);
  };

  const fetchGPS = async () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    setQuickOpen(false);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude: lat, longitude: lon } = pos.coords;
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        if (data?.display_name) onChange(data.display_name);
      } catch {}
      setGpsLoading(false);
    }, () => setGpsLoading(false), { timeout: 8000 });
  };

  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestOpen(false);
        setQuickOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasSuggest = suggestOpen && suggestions.length > 0;
  const accentColor = accent || '#6366f1';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          type="text"
          placeholder={field.placeholder || ''}
          value={value || ''}
          autoComplete="off"
          onChange={e => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
          onFocus={() => suggestions.length > 0 && setSuggestOpen(true)}
          onClick={e => e.stopPropagation()}
          className="kk-of-input"
          style={{
            background: inputBg,
            border: `1.5px solid ${borderColor}`,
            borderRadius: hasSuggest ? '8px 8px 0 0' : 8,
            padding: '0.42rem 4rem 0.42rem 0.65rem',
            color: textColor,
            fontSize: '0.72rem',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
        />
        {/* Right-side icon controls */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center' }}>
          {/* GPS button */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); fetchGPS(); }}
            title="Use my current location"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px',
              color: gpsLoading ? accentColor : `${textColor}80`, display: 'flex', alignItems: 'center' }}>
            <Navigation size={14} style={{ animation: gpsLoading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          {/* Divider */}
          <div style={{ width: 1, height: '60%', background: borderColor }} />
          {/* Quick-pick chevron */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setQuickOpen(o => !o); setSuggestOpen(false); }}
            title="Quick pick"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px',
              color: quickOpen ? accentColor : `${textColor}80`, display: 'flex', alignItems: 'center' }}>
            <ChevronDown size={14} style={{ transition: 'transform 0.15s', transform: quickOpen ? 'rotate(180deg)' : 'none' }} />
          </button>
        </div>
      </div>

      {/* Nominatim autocomplete dropdown */}
      {hasSuggest && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: dropdownBg, border: `1px solid ${borderColor}`, borderTop: 'none',
          borderRadius: '0 0 8px 8px', zIndex: 200, maxHeight: 160, overflowY: 'auto',
          boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
        }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onChange(s); setSuggestions([]); setSuggestOpen(false); }}
              style={{ padding: '0.42rem 0.65rem', fontSize: '0.68rem', color: textColor, cursor: 'pointer',
                borderBottom: i < suggestions.length - 1 ? `1px solid ${borderColor}` : 'none',
                lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s}
            </div>
          ))}
        </div>
      )}

      {/* Quick-pick dropdown */}
      {quickOpen && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', top: '100%', right: 0,
          background: dropdownBg, border: `1px solid ${borderColor}`,
          borderRadius: 8, zIndex: 201, minWidth: 150,
          boxShadow: '0 6px 16px rgba(0,0,0,0.4)', overflow: 'hidden',
        }}>
          {shopName && (
            <div onMouseDown={e => { e.preventDefault(); onChange(`At ${shopName}`); setQuickOpen(false); }}
              style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem', color: textColor, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.45rem',
                borderBottom: `1px solid ${borderColor}` }}
              onMouseEnter={e => e.currentTarget.style.background = `${accentColor}18`}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <Building2 size={13} color={accentColor} />
              At {shopName}
            </div>
          )}
          <div onMouseDown={e => { e.preventDefault(); setQuickOpen(false); fetchGPS(); }}
            style={{ padding: '0.5rem 0.75rem', fontSize: '0.72rem', color: textColor, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.45rem' }}
            onMouseEnter={e => e.currentTarget.style.background = `${accentColor}18`}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <MapPin size={13} color={accentColor} />
            My Place
          </div>
        </div>
      )}
    </div>
  );
}

function TextareaField({ field, value, onChange, textColor, inputBg, borderColor }) {
  return (
    <textarea
      placeholder={field.placeholder || ''}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      rows={3}
      className="kk-of-input"
      style={{
        background: inputBg,
        border: `1.5px solid ${borderColor}`,
        borderRadius: 8,
        padding: '0.42rem 0.65rem',
        color: textColor,
        fontSize: '0.72rem',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        resize: 'none',
        fontFamily: 'inherit',
        lineHeight: 1.45,
      }}
    />
  );
}

function SelectField({ field, value, onChange, textColor, inputBg, borderColor }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      className="kk-of-input"
      style={{
        background: inputBg,
        border: `1.5px solid ${borderColor}`,
        borderRadius: 8,
        padding: '0.42rem 0.65rem',
        color: value ? textColor : `${textColor}88`,
        fontSize: '0.72rem',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <option value="">Select…</option>
      {(field.options || []).map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function ChipSelect({ field, value, onChange, accent, textColor, borderColor }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
      {(field.options || []).map(opt => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={e => { e.stopPropagation(); onChange(active ? '' : opt); }}
            style={{
              padding: '0.28rem 0.65rem',
              borderRadius: 20,
              border: `1.5px solid ${active ? accent : borderColor}`,
              background: active ? `${accent}22` : 'transparent',
              color: active ? accent : `${textColor}A6`,
              fontSize: '0.68rem',
              fontWeight: active ? 700 : 400,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function StepperField({ field, value, onChange, accent, textColor, borderColor }) {
  const min = field.min ?? 0;
  const max = field.max ?? 999;
  const num = typeof value === 'number' ? value : (field.defaultVal ?? min);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <button
        onClick={e => { e.stopPropagation(); onChange(Math.max(min, num - 1)); }}
        disabled={num <= min}
        style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `1.5px solid ${borderColor}`,
          background: `${textColor}12`,
          color: textColor, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: num <= min ? 0.35 : 1,
        }}
      ><Minus size={12} /></button>
      <span style={{ minWidth: 24, textAlign: 'center', fontSize: '0.82rem', fontWeight: 700, color: textColor }}>{num}</span>
      <button
        onClick={e => { e.stopPropagation(); onChange(Math.min(max, num + 1)); }}
        disabled={num >= max}
        style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `1.5px solid ${accent}`,
          background: `${accent}18`,
          color: accent, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: num >= max ? 0.35 : 1,
        }}
      ><Plus size={12} /></button>
    </div>
  );
}

function ToggleField({ field, value, onChange, accent, textColor }) {
  const on = !!value;
  return (
    <button
      onClick={e => { e.stopPropagation(); onChange(!on); }}
      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <div style={{
        width: 36, height: 20, borderRadius: 10, position: 'relative',
        background: on ? accent : `${textColor}26`,
        transition: 'background 0.2s', flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute', top: 2, left: on ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%',
          background: '#fff', transition: 'left 0.2s',
        }} />
      </div>
      <span style={{ fontSize: '0.7rem', color: on ? accent : `${textColor}80` }}>
        {on ? 'Yes' : 'No'}
      </span>
    </button>
  );
}

function FieldLabel({ label, required, labelColor }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: labelColor, marginBottom: '0.22rem', letterSpacing: '0.03em' }}>
      {label}{required && <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const OrderFormPanel = ({
  item,
  isAuthenticated,
  visible,
  onClose,
  onSubmit,
  onGoogleSubmit,
  onGuestSubmit,
  guestCheckoutFields,
  theme,
  mode = 'card',
  onTotalChange,
}) => {
  const accent       = theme?.colors?.accent     || '#800000';
  const bgColor      = theme?.colors?.background || '#141414';
  const textColor    = theme?.colors?.text       || '#ffffff';
  const panelBg      = tintColor(bgColor, 25);
  const headerBg     = tintColor(bgColor, 15);
  const dropdownBg   = tintColor(bgColor, 45);
  const inputBg      = tintColor(bgColor, 40);
  const inputBorder  = `${accent}99`;           // accent at ~60% — bold, themed
  const borderColor  = `${accent}55`;           // slightly softer for dividers/chips
  const labelColor   = textColor;               // full-brightness labels

  const isProduct = !item?.isService && !item?.jobType && !item?.serviceTab;
  const isGigClaim = item?.serviceTab === 'need';
  // 'hourly' (default) multiplies price × duration;
  // 'daily' multiplies price × the pick-up/drop-off date span;
  // 'total' treats the listed price as a flat rate — no duration needed.
  const isFlatRate = item?.pricingType === 'total';
  const isDaily = item?.pricingType === 'daily';
  // Flat-fee services have no meaningful duration to collect from the customer.
  const showDuration = !isProduct && !isGigClaim && !isFlatRate;
  const rateSuffix = !showDuration ? '' : isDaily ? '/day' : '/hr';

  // Drop a bare ".00" (e.g. "$45.00/hr" → "$45/hr") but keep real cents
  // (e.g. "$45.50/hr" stays as-is).
  const formatRate = (price) => {
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    return num % 1 === 0 ? `${num}` : num.toFixed(2);
  };

  const fields = getItemFormFields(item);
  const [values, setValues] = useState(() => initValues(fields));
  const [durationHours, setDurationHours] = useState(1);
  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const scrollRef = useRef(null);

  // Guest checkout — name is always collected; everything else is whatever
  // the shop enabled in Shop Settings. Kept in a separate state (not reset
  // when `item` changes) so browsing between items doesn't clear what a
  // guest already typed. Keys are prefixed "guest" so they can never
  // collide with an item-specific field key.
  const guestFields = !isAuthenticated ? [
    { key: 'guestName', label: 'Your Name', type: 'text', required: true, placeholder: 'Jane Doe' },
    ...(guestCheckoutFields?.phone?.enabled ? [{ key: 'guestPhone', label: 'Phone Number', type: 'text', required: !!guestCheckoutFields.phone.required, placeholder: '(555) 123-4567' }] : []),
    ...(guestCheckoutFields?.email?.enabled ? [{ key: 'guestEmail', label: 'Email Address', type: 'text', required: !!guestCheckoutFields.email.required, placeholder: 'jane@example.com' }] : []),
    ...(guestCheckoutFields?.address?.enabled ? [{ key: 'guestAddress', label: 'Delivery/Service Address', type: 'text', required: !!guestCheckoutFields.address.required, placeholder: '123 Main St' }] : []),
    ...(guestCheckoutFields?.notes?.enabled ? [{ key: 'guestNotes', label: 'Order Notes', type: 'textarea', required: !!guestCheckoutFields.notes.required, placeholder: "Anything the seller should know…" }] : []),
  ] : [];
  const [guestValues, setGuestValues] = useState({});
  const allFields = [...fields, ...guestFields];

  const days = (() => {
    if (!pickupDate || !dropoffDate) return 0;
    const d1 = new Date(`${pickupDate}T00:00:00`);
    const d2 = new Date(`${dropoffDate}T00:00:00`);
    const diff = Math.round((d2 - d1) / 86400000);
    return diff > 0 ? diff : 0;
  })();

  const unitPrice = parseFloat(item?.price) || 0;
  const computedTotal = isFlatRate ? unitPrice : isDaily ? unitPrice * days : unitPrice * durationHours;

  useEffect(() => {
    setValues(initValues(getItemFormFields(item)));
    setDurationHours(1);
    setPickupDate('');
    setDropoffDate('');
  }, [item?.id]);

  useEffect(() => {
    if (visible && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [visible]);

  // Let the parent (the card's own "Order" CTA) track the live duration/date-aware
  // total so it can stay in sync instead of showing a stale flat price.
  useEffect(() => {
    if (!showDuration) return;
    onTotalChange?.(computedTotal);
  }, [computedTotal, showDuration, onTotalChange]);

  const set = (key, val) => {
    if (key.startsWith('guest')) setGuestValues(prev => ({ ...prev, [key]: val }));
    else setValues(prev => ({ ...prev, [key]: val }));
  };
  const getValue = (key) => (key.startsWith('guest') ? guestValues[key] : values[key]);

  const requiredFields = fields.filter(f => f.required);
  // Item-field validity alone — unchanged from before guest fields existed.
  // This is what gates "Sign in with Google" (a real account doesn't need
  // the guest contact fields pre-filled).
  const canSubmit = requiredFields.every(f => {
    const v = values[f.key];
    return v !== '' && v !== null && v !== undefined;
  }) && (!isDaily || days > 0);

  const requiredGuestFields = guestFields.filter(f => f.required);
  // Gates "Place Order" (the no-login path): item fields AND guest fields.
  const canSubmitGuest = canSubmit && requiredGuestFields.every(f => {
    const v = guestValues[f.key];
    return v !== '' && v !== null && v !== undefined;
  });

  const itemLabel = item?.jobType === 'rides' ? 'Ride'
    : item?.isService ? 'Service'
    : 'Order';
  const isQuote = item?.isService && (item?.serviceTab || 'offer') !== 'need'
    && (!item?.price || parseFloat(item?.price) === 0);

  const hasFields = fields.length > 0 || guestFields.length > 0;

  const buildFormData = () => ({
    ...values,
    ...(!showDuration ? {} : isDaily
      ? { pickupDate, dropoffDate, durationDays: days, pricingType: 'daily', totalPrice: computedTotal }
      : { durationHours, pricingType: isFlatRate ? 'total' : 'hourly', totalPrice: computedTotal }),
  });

  const handleAction = () => {
    if (isAuthenticated) {
      if (!canSubmit) return;
      onSubmit?.(buildFormData());
      return;
    }
    if (!canSubmitGuest) return;
    onGuestSubmit?.({
      ...buildFormData(),
      guestInfo: {
        name: guestValues.guestName,
        phone: guestValues.guestPhone || '',
        email: guestValues.guestEmail || '',
        address: guestValues.guestAddress || '',
        notes: guestValues.guestNotes || '',
      },
    });
  };

  const handleGoogleAction = () => {
    if (!canSubmit) return;
    onGoogleSubmit?.(buildFormData());
  };

  const fp = { accent, textColor, inputBg, borderColor: inputBorder, labelColor, dropdownBg, shopName: item?.shopName || '' };

  // Inline mode: renders as a plain block inside the zoom card (no overlay / backdrop)
  if (mode === 'inline' && !visible) return null;

  // Inline + no other fields: compact sticky button bar at the bottom of the zoom area.
  // Daily-priced items always need the pick-up/drop-off pickers, so they skip this shortcut.
  if (mode === 'inline' && !hasFields && !isDaily) {
    if (!isAuthenticated) return null;
    return (
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: panelBg,
          borderTop: `2px solid ${accent}`,
          padding: '0.45rem 0.8rem 0.7rem',
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.35rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: textColor, opacity: 0.8 }}>
            {item?.name}{item?.price ? ` · $${formatRate(item.price)}${rateSuffix}` : ''}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: `${textColor}66`, cursor: 'pointer', padding: '0.1rem', display: 'flex' }}>
            <X size={14} />
          </button>
        </div>
        <button
          onClick={handleAction}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
            width: '100%', padding: '0.55rem',
            background: accent,
            border: 'none',
            borderRadius: 10,
            color: '#fff',
            fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
            letterSpacing: '0.02em', minHeight: 42,
          }}
        >
          Order
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={mode === 'inline' ? {} : {
        position: 'absolute', inset: 0, zIndex: 20,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Scoped styles for placeholder + focus */}
      <style>{`
        .kk-of-input::placeholder { color: ${textColor}88 !important; opacity: 1; }
        .kk-of-input:focus { border-color: ${accent} !important; box-shadow: 0 0 0 2px ${accent}33; }
      `}</style>

      {/* Backdrop — overlay mode only */}
      {mode !== 'inline' && (
        <div
          onClick={onClose}
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.7)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.25s ease',
            zIndex: 0,
          }}
        />
      )}

      {/* Panel */}
      <div style={mode === 'inline' ? {
        background: panelBg,
        borderTop: `2px solid ${accent}`,
        display: 'flex', flexDirection: 'column',
      } : {
        position: 'relative', zIndex: 1,
        background: panelBg,
        borderTop: `2px solid ${accent}`,
        borderRadius: '12px 12px 0 0',
        transform: visible ? 'translateY(0)' : 'translateY(102%)',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '85%',
        overflow: 'hidden',
        boxShadow: '0 -4px 30px rgba(0,0,0,0.5)',
      }}>

        {/* Header — hidden for pure products */}
        {!isProduct && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.6rem 0.8rem 0.5rem',
            borderBottom: `1px solid ${borderColor}`,
            background: headerBg,
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <div style={{ width: 3, height: 15, borderRadius: 2, background: accent }} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: textColor }}>
                {hasFields ? `Book / ${itemLabel}` : `Place ${itemLabel}`}
              </span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: `${textColor}66`, cursor: 'pointer', padding: 2, display: 'flex' }}>
              <X size={15} />
            </button>
          </div>
        )}

        {/* Scrollable body — fields only, no submit button */}
        <div
          ref={scrollRef}
          onClick={e => e.stopPropagation()}
          style={{
            overflowY: 'auto', flex: 1,
            padding: '0.7rem 0.8rem 0.5rem',
            display: 'flex', flexDirection: 'column', gap: '0.6rem',
            background: panelBg,
          }}
        >
          {/* Product: close button + item summary row */}
          {isProduct && (
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.1rem' }}>
              <div>
                <div style={{ fontSize: '0.84rem', fontWeight: 700, color: textColor, lineHeight: 1.3 }}>{item?.name}</div>
                {item?.price && <div style={{ fontSize: '0.72rem', color: accent, marginTop: '0.15rem' }}>${item.price}</div>}
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: `${textColor}66`, cursor: 'pointer', padding: '0.2rem', display: 'flex', flexShrink: 0 }}>
                <X size={15} />
              </button>
            </div>
          )}

          {/* Item pill — hidden for products */}
          {!isProduct && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              background: `${accent}20`, border: `1px solid ${accent}45`,
              borderRadius: 20, padding: '0.25rem 0.75rem',
              fontSize: '0.68rem', fontWeight: 600, color: accent,
              alignSelf: 'flex-start',
            }}>
              {item?.name || 'Item'}
              {item?.price ? <span style={{ opacity: 0.75 }}>· ${item.price}{rateSuffix}</span> : null}
            </div>
          )}

          {/* Dynamic fields — item-specific fields, plus (for guests) the
              seller-configured contact fields appended at the end */}
          {allFields.map(field => (
            <div key={field.key}>
              <FieldLabel label={field.label} required={field.required} labelColor={labelColor} />
              {(field.type === 'location' || field.isAddress || ADDRESS_KEYS.has(field.key)) ? (
                <AddressField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : (field.type === 'text' || field.type === 'datetime-local' || field.type === 'date') ? (
                <TextField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : field.type === 'textarea' ? (
                <TextareaField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : field.type === 'select' ? (
                <SelectField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : field.type === 'chip-select' ? (
                <ChipSelect field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : field.type === 'stepper' ? (
                <StepperField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : field.type === 'toggle' ? (
                <ToggleField field={field} value={getValue(field.key)} onChange={v => set(field.key, v)} {...fp} />
              ) : null}
            </div>
          ))}

          {/* ── Duration (hours) / Rental Dates (daily) ─────────────────── */}
          {showDuration && (
            <div style={{
              background: `${accent}10`,
              border: `1.5px solid ${inputBorder}`,
              borderRadius: 10,
              padding: '0.6rem 0.75rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: textColor }}>
                  {isDaily ? 'Rental Dates' : 'Duration'}
                </span>
                {item?.price && (!isDaily || days > 0) && (
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: accent }}>
                    ${computedTotal.toFixed(0)} total
                  </span>
                )}
              </div>

              {isDaily ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.62rem', fontWeight: 600, color: labelColor, marginBottom: '0.2rem' }}>Pick-up Date</div>
                      <input
                        type="date"
                        value={pickupDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                          const v = e.target.value;
                          setPickupDate(v);
                          if (dropoffDate && v && dropoffDate <= v) setDropoffDate('');
                        }}
                        className="kk-of-input"
                        style={{ background: inputBg, border: `1.5px solid ${inputBorder}`, borderRadius: 8, padding: '0.42rem 0.5rem', color: textColor, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.62rem', fontWeight: 600, color: labelColor, marginBottom: '0.2rem' }}>Drop-off Date</div>
                      <input
                        type="date"
                        value={dropoffDate}
                        min={pickupDate || new Date().toISOString().slice(0, 10)}
                        disabled={!pickupDate}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setDropoffDate(e.target.value)}
                        className="kk-of-input"
                        style={{ background: inputBg, border: `1.5px solid ${inputBorder}`, borderRadius: 8, padding: '0.42rem 0.5rem', color: textColor, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', opacity: pickupDate ? 1 : 0.5 }}
                      />
                    </div>
                  </div>
                  <div style={{ fontSize: '0.62rem', color: `${textColor}70`, marginTop: '0.35rem', lineHeight: 1.4 }}>
                    {days > 0
                      ? `${days} day${days === 1 ? '' : 's'} · $${unitPrice}/day`
                      : 'Choose pick-up and drop-off dates'}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                    <button
                      onClick={e => { e.stopPropagation(); setDurationHours(h => Math.max(1, h - 1)); }}
                      disabled={durationHours <= 1}
                      style={{
                        width: 30, height: 30, borderRadius: '50%',
                        border: `1.5px solid ${borderColor}`,
                        background: `${textColor}12`,
                        color: textColor, cursor: durationHours <= 1 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: durationHours <= 1 ? 0.35 : 1, flexShrink: 0,
                      }}
                    ><Minus size={13} /></button>

                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: 800, color: textColor }}>{durationHours}</span>
                      <span style={{ fontSize: '0.72rem', color: labelColor, marginLeft: '0.3rem' }}>
                        {durationHours === 1 ? 'hour' : 'hours'}
                      </span>
                    </div>

                    <button
                      onClick={e => { e.stopPropagation(); setDurationHours(h => Math.min(24, h + 1)); }}
                      disabled={durationHours >= 24}
                      style={{
                        width: 30, height: 30, borderRadius: '50%',
                        border: `1.5px solid ${accent}`,
                        background: `${accent}18`,
                        color: accent, cursor: durationHours >= 24 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: durationHours >= 24 ? 0.35 : 1, flexShrink: 0,
                      }}
                    ><Plus size={13} /></button>
                  </div>

                  <div style={{ fontSize: '0.62rem', color: `${textColor}70`, marginTop: '0.35rem', lineHeight: 1.4 }}>
                    {isFlatRate
                      ? `${durationHours === 1 ? '1 hr' : `${durationHours} hr`} session · flat rate, total stays the same regardless of duration`
                      : durationHours === 1
                        ? '1 hr session · tap + to book more hours'
                        : `${durationHours} hr session · each hour = 1 additional booking slot`}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Action button — sticky footer outside the scroll area ── */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            padding: '0.5rem 0.8rem 0.8rem',
            background: panelBg,
            flexShrink: 0,
            borderTop: hasFields ? `1px solid ${borderColor}` : 'none',
            // Sticks to the bottom of the nearest scrolling ancestor (ZoomContainer in inline mode)
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
          }}
        >
          {isAuthenticated ? (
            <>
              <button
                onClick={handleAction}
                disabled={!canSubmit}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
                  width: '100%',
                  padding: '0.6rem',
                  background: canSubmit ? accent : `${textColor}14`,
                  border: 'none',
                  borderRadius: 10,
                  color: canSubmit ? '#fff' : `${textColor}40`,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  transition: 'background 0.15s, opacity 0.15s',
                  letterSpacing: '0.02em',
                  opacity: canSubmit ? 1 : 0.55,
                  minHeight: 44,
                }}
              >
                Place Order
              </button>
              {!canSubmit && (requiredFields.length > 0 || (isDaily && days === 0)) && (
                <div style={{ fontSize: '0.6rem', color: `${textColor}4D`, textAlign: 'center', marginTop: '0.3rem' }}>
                  {isDaily && days === 0 && requiredFields.length === 0 ? 'Choose pick-up and drop-off dates to continue' : 'Fill in required fields to continue'}
                </div>
              )}
            </>
          ) : (
            <>
              {/* No login required — order with the contact info filled in above */}
              <button
                onClick={handleAction}
                disabled={!canSubmitGuest}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
                  width: '100%',
                  padding: '0.6rem',
                  background: canSubmitGuest ? accent : `${textColor}14`,
                  border: 'none',
                  borderRadius: 10,
                  color: canSubmitGuest ? '#fff' : `${textColor}40`,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: canSubmitGuest ? 'pointer' : 'not-allowed',
                  transition: 'background 0.15s, opacity 0.15s',
                  letterSpacing: '0.02em',
                  opacity: canSubmitGuest ? 1 : 0.55,
                  minHeight: 44,
                }}
              >
                Place Order
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
                <div style={{ flex: 1, height: 1, background: borderColor }} />
                <span style={{ fontSize: '0.64rem', color: `${textColor}60`, fontWeight: 600 }}>or</span>
                <div style={{ flex: 1, height: 1, background: borderColor }} />
              </div>

              <button
                onClick={handleGoogleAction}
                disabled={!canSubmit}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
                  width: '100%',
                  padding: '0.55rem',
                  background: 'transparent',
                  border: `1.5px solid ${canSubmit ? inputBorder : `${textColor}20`}`,
                  borderRadius: 10,
                  color: canSubmit ? textColor : `${textColor}40`,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  transition: 'background 0.15s, opacity 0.15s',
                  letterSpacing: '0.02em',
                  opacity: canSubmit ? 1 : 0.55,
                  minHeight: 40,
                }}
              >
                <img src={GOOGLE_LOGO} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
                Order with Google
              </button>

              {!canSubmitGuest && (requiredFields.length > 0 || requiredGuestFields.length > 0 || (isDaily && days === 0)) && (
                <div style={{ fontSize: '0.6rem', color: `${textColor}4D`, textAlign: 'center', marginTop: '0.3rem' }}>
                  {isDaily && days === 0 && requiredFields.length === 0 ? 'Choose pick-up and drop-off dates to continue' : 'Fill in required fields to continue'}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderFormPanel;
